import examples from "libphonenumber-js/examples.mobile";
import { getExampleNumber, parsePhoneNumberWithError, type PhoneNumber, type PhoneNumberType, validatePhoneNumberLength, type ValidatePhoneNumberLengthResult } from "libphonenumber-js/max";
import { areaCode, destinationCode } from "./area";
import { findRegion, HOME_REGION, mainRegion, type Region } from "./regions";
import { type Short, shortReading } from "./short";

export type Result =
  | { kind: "blank" }
  | { kind: "error"; message: string }
  | { kind: "short"; short: Short }
  | { kind: "reading"; reading: Reading };

export interface Reading {
  // Which region the digits turned out to name, which is not always the one they were read against: a number typed
  // with a plus answers for itself, and one that answers for nowhere at all leaves this undefined.
  region?: Region;
  callingCode: string;
  nationalNumber: string;
  // The digits that name where the line is, and the digits that route to it, which are the same digits whenever the
  // number is somewhere at all: a mobile has the second and not the first.
  areaCode: string;
  destinationCode: string;
  extension: string;
  // The operator somebody dialled in front of the number to carry the call, which only Brazil and Argentina have.
  carrierCode: string;
  valid: boolean;
  possibility: string;
  type: string;
  formats: Format[];
  // The parsed number itself, which is what the geocoder is handed; nothing on screen reads it directly.
  number: PhoneNumber;
}

export interface Format {
  label: string;
  value: string;
}

export function readNumber(input: string, region: Region): Result {
  const text = input.trim();
  if (!text) return { kind: "blank" };

  let parsed: PhoneNumber;
  try {
    parsed = parsePhoneNumberWithError(text, region.code);
  } catch (cause) {
    return { kind: "error", message: errorMessage(cause) };
  }

  // A short code is answered as one and never as a phone number that failed: 000 has no international form at all,
  // and writing it down as +61000 would be handing somebody digits that reach nothing. A number valid in its own
  // right is never asked, the two being exclusive and a real number's own reading being the fuller one.
  const code = parsed.isValid() ? undefined : shortReading(parsed.nationalNumber, region);
  return code ? { kind: "short", short: code } : { kind: "reading", reading: reading(text, parsed, region) };
}

function reading(text: string, parsed: PhoneNumber, region: Region): Reading {
  // Nothing at all is what the library answers with for a length numbers there are given, which is the whole of what
  // "possible" means; every other answer is the one way this number's length failed.
  const length = validatePhoneNumberLength(text, region.code);

  return {
    region: numberRegion(parsed),
    callingCode: `+${parsed.countryCallingCode}`,
    nationalNumber: parsed.nationalNumber,
    areaCode: areaCode(parsed),
    destinationCode: destinationCode(parsed),
    extension: parsed.ext ?? "",
    carrierCode: parsed.carrierCode ?? "",
    valid: parsed.isValid(),
    possibility: length ? POSSIBILITIES[length] ?? UNKNOWN : "Possible",
    type: TYPES[parsed.getType() ?? ""] ?? UNKNOWN,
    formats: formats(parsed),
    number: parsed,
  };
}

// A number complete enough names its own country. One that is not says only what its calling code says, and that is
// still a country for every code but the global ones — +800 belongs to none, which is what leaves this undefined.
function numberRegion(parsed: PhoneNumber): Region | undefined {
  const possible = parsed.getPossibleCountries();
  const named = parsed.country ?? (possible.length === 1 ? possible[0] : undefined);
  if (named) return findRegion(named);
  return parsed.isNonGeographic() ? undefined : findRegion(mainRegion(parsed.countryCallingCode));
}

function formats(parsed: PhoneNumber): Format[] {
  const national = parsed.formatNational();
  const abroad = fromHome(parsed);

  return [
    { label: "E.164", value: parsed.format("E.164") },
    { label: "International", value: parsed.formatInternational() },
    { label: "National", value: national },
    { label: "RFC 3966", value: parsed.format("RFC3966") },
    // The digits somebody would actually punch in from where they are, international prefix and all — which for a
    // number in their own country is the national format over again, and an empty value is how a row is dropped.
    { label: `Dialling from ${HOME_REGION.name}`, value: abroad === national ? "" : abroad },
  ];
}

function fromHome(parsed: PhoneNumber): string {
  try {
    return parsed.format("IDD", { fromCountry: HOME_REGION.code });
  } catch {
    // A region with no international prefix of its own is one nobody can be told how to dial out of.
    return "";
  }
}

// What a number here looks like, which is a better prompt than an invented one and is the library's own answer: it
// ships an example per region and a mobile is what somebody typing into this box is most likely holding.
export function exampleNumber(region: Region): string {
  return getExampleNumber(region.code, examples)?.formatNational() ?? "";
}

const UNKNOWN = "Unknown";

const TYPES: Record<string, string> = {
  FIXED_LINE: "Fixed line",
  MOBILE: "Mobile",
  // Where a country's fixed and mobile numbers are drawn from one range, which is the case across North America.
  FIXED_LINE_OR_MOBILE: "Fixed line or mobile",
  TOLL_FREE: "Toll free",
  PREMIUM_RATE: "Premium rate",
  SHARED_COST: "Shared cost",
  VOIP: "VoIP",
  PERSONAL_NUMBER: "Personal number",
  PAGER: "Pager",
  UAN: "Universal access number",
  VOICEMAIL: "Voicemail",
} satisfies Record<PhoneNumberType, string>;

// Whether the length could belong to a number here at all, which is a weaker question than whether this one is real:
// a number can be possible and still invalid, and saying which of the two failed is the useful half.
const POSSIBILITIES: Record<ValidatePhoneNumberLengthResult, string> = {
  INVALID_COUNTRY: "No country has that calling code",
  NOT_A_NUMBER: "Not a number",
  TOO_SHORT: "Too short",
  TOO_LONG: "Too long",
  INVALID_LENGTH: "Not a length numbers here are given",
};

// The library throws the name of what went wrong, which is written for a `switch` and not for somebody mid-way
// through typing their own number. Keyed on what it threw rather than replaced, so a name it stops using stops
// matching here too and falls through to its own word rather than to a wrong translation of it.
const MESSAGES: Record<string, string> = {
  NOT_A_NUMBER: "Not a phone number",
  INVALID_COUNTRY: "No country has that calling code",
  TOO_SHORT: "Too short to be a phone number",
  TOO_LONG: "Too long to be a phone number",
};

function errorMessage(cause: unknown): string {
  const thrown = cause instanceof Error ? cause.message : String(cause);
  return MESSAGES[thrown] ?? thrown;
}
